"use client";

import { motion } from "framer-motion";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useState } from "react";
import Link from "next/link";
import { useAuth } from "@/lib/auth-context";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { login, loading } = useAuth();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      await login(email, password);
    } catch (err: any) {
      setError(err.message || "E-mail ou senha incorretos");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <main className="min-h-[100dvh] w-full bg-[#f4f6f9] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#2563eb] animate-spin" />
      </main>
    );
  }

  return (
    <main className="min-h-[100dvh] w-full bg-[#f4f6f9] flex flex-col relative font-sans overflow-hidden items-center p-4">
      {/* App Name Header */}
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="pt-12 pb-8 flex justify-center items-center shrink-0"
      >
        <h1 className="text-3xl font-bold text-[#1e3a8a] tracking-tight text-center">
          Luna chat
        </h1>
      </motion.div>

      {/* Main Login Card */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
        className="bg-white rounded-[32px] px-7 pt-10 pb-10 shadow-sm w-full max-w-md flex flex-col"
      >
        <div className="text-center mb-10">
          <h2 className="text-[22px] font-bold text-gray-900 leading-[1.3]">
            Bem-vindo ao<br />
            Luna chat! Comece agora.
          </h2>
        </div>

        <form className="space-y-6 flex flex-col" onSubmit={handleLogin}>
          <div className="space-y-2">
            <label className="block text-[13px] font-medium text-gray-900">
              E-mail
            </label>
            <input
              type="email"
              placeholder="joedoe75@gmail.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full h-[52px] px-5 text-[15px] text-gray-900 bg-[#f7f8f9] rounded-2xl outline-none placeholder:text-gray-400 focus:bg-[#f0f2f5] transition-colors"
            />
          </div>

          <div className="space-y-2 relative">
            <label className="block text-[13px] font-medium text-gray-900">
              Senha
            </label>
            <div className="relative flex items-center">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full h-[52px] pl-5 pr-12 text-[15px] text-gray-900 bg-[#f7f8f9] rounded-2xl outline-none placeholder:text-gray-400 focus:bg-[#f0f2f5] transition-colors"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 text-gray-400 hover:text-gray-600 focus:outline-none transition-colors p-1"
                aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
              >
                {showPassword ? (
                  <EyeOff className="w-5 h-5" strokeWidth={1.5} />
                ) : (
                  <Eye className="w-5 h-5" strokeWidth={1.5} />
                )}
              </button>
            </div>
          </div>

          {error && (
            <motion.p
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-sm text-red-500 font-medium text-center"
            >
              {error}
            </motion.p>
          )}

          <div className="pt-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full h-[52px] bg-[#2563eb] text-white text-[16px] font-medium rounded-full hover:bg-[#1d4ed8] active:scale-[0.98] transition-all focus:outline-none focus:ring-2 focus:ring-[#2563eb] focus:ring-offset-2 shadow-sm disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSubmitting && <Loader2 className="w-5 h-5 animate-spin" />}
              {isSubmitting ? "Entrando..." : "Entrar"}
            </button>
          </div>

          <p className="text-center text-sm text-gray-400 pt-2">
            Não tem conta?{" "}
            <Link href="/register" className="text-[#2563eb] font-semibold hover:underline">
              Criar conta
            </Link>
          </p>
        </form>
      </motion.div>
    </main>
  );
}
